import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { DEFAULT_USD_GEL_RATE, DEFAULT_EUR_GEL_RATE, DEFAULT_CURRENCY_RATES } from './constants';

/**
 * Utility function for merging Tailwind CSS classes
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * URL ვალიდაცია - უსაფრთხოებისთვის
 * ამოწმებს რომ URL არის უსაფრთხო (http/https)
 */
export function isValidUrl(url) {
  if (!url || typeof url !== 'string') return false;
  
  try {
    // დავამატოთ protocol თუ არ არის
    const urlToCheck = url.startsWith('http') ? url : `https://${url}`;
    const parsed = new URL(urlToCheck);
    
    // მხოლოდ http და https პროტოკოლები
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

/**
 * URL სანიტაიზაცია - XSS პრევენციისთვის
 * აბრუნებს უსაფრთხო URL-ს ან null-ს
 */
export function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') return null;
  
  try {
    const urlToCheck = url.startsWith('http') ? url : `https://${url}`;
    const parsed = new URL(urlToCheck);
    
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return null;
    }
    
    return parsed.href;
  } catch {
    return null;
  }
}

/**
 * პლატფორმის ავტომატური ამოცნობა ლინკიდან
 */
export function detectPlatform(url) {
  if (!url || typeof url !== 'string') return 'Unknown';
  
  // ჯერ შევამოწმოთ URL-ის ვალიდურობა
  if (!isValidUrl(url)) {
    return 'Invalid URL';
  }
  
  const urlLower = url.toLowerCase();

  const platforms = {
    'Amazon': ['amazon.com', 'amazon.co.uk', 'amazon.de', 'amazon.fr', 'amazon.it', 'amazon.es', 'amazon.ca', 'amazon.com.au'],
    'eBay': ['ebay.com', 'ebay.co.uk', 'ebay.de', 'ebay.fr', 'ebay.it'],
    'Shein': ['shein.com', 'shein.co.uk', 'shein.fr'],
    'TikTok Shop': ['tiktok.com', 'vt.tiktok.com', 'vm.tiktok.com'],
    'Instagram': ['instagram.com', 'instagr.am'],
    'Facebook': ['facebook.com', 'fb.com', 'fb.marketplace.com'],
    'Etsy': ['etsy.com'],
    'AliExpress': ['aliexpress.com', 'aliexpress.ru'],
    'Temu': ['temu.com'],
    'Walmart': ['walmart.com'],
    'Target': ['target.com'],
    'ASOS': ['asos.com'],
    'Zara': ['zara.com'],
    'H&M': ['hm.com', 'hm.com'],
    'Nike': ['nike.com'],
    'Adidas': ['adidas.com'],
    'Apple': ['apple.com'],
    'Best Buy': ['bestbuy.com'],
    'Wayfair': ['wayfair.com'],
    'HomeGoods': ['homegoods.com'],
    'IKEA': ['ikea.com'],
  };

  for (const [platform, domains] of Object.entries(platforms)) {
    if (domains.some(domain => urlLower.includes(domain))) {
      return platform;
    }
  }

  return 'Other';
}

/**
 * ლინკის მოკლე ვერსიის შექმნა
 */
export function shortenUrl(url, maxLength = 30) {
  if (!url || url.length <= maxLength) return url;
  return url.substring(0, maxLength) + '...';
}

/**
 * ტრეკინგ კოდის ფორმატირება
 */
export function formatTrackingCode(code) {
  if (!code) return '';
  return code.trim().toUpperCase();
}

/**
 * სტატუსის თარგმანი ქართულად
 */
export function translateStatus(status) {
  const translations = {
    'ordered': 'გამოწერილია',
    'at_carrier': 'გადამზიდავში',
    'shipped': 'გამოგზავნილია',
    'arrived': 'ჩამოტანილია',
    'at_home': 'სახლში/საწყობში',
    'delivered_to_client': 'გაცემული'
  };
  return translations[status] || status;
}

/**
 * სტატუსის ფერის დადგენა
 */
export function getStatusColor(status) {
  const colors = {
    'ordered': 'bg-blue-500',
    'at_carrier': 'bg-orange-500',
    'shipped': 'bg-yellow-500',
    'arrived': 'bg-purple-500',
    'at_home': 'bg-teal-500',
    'delivered_to_client': 'bg-emerald-500'
  };
  return colors[status] || 'bg-gray-500';
}

/**
 * ლიმიტის სტატუსის ფერის დადგენა
 */
export function getLimitStatusColor(status) {
  const colors = {
    'ok': 'text-green-500',
    'warning': 'text-yellow-500',
    'exceeded': 'text-red-500'
  };
  return colors[status] || 'text-gray-500';
}

/**
 * ფასის კონვერტაცია USD -> GEL (მიახლოებითი კურსი)
 */
export function convertUsdToGel(usd, rate = DEFAULT_USD_GEL_RATE) {
  if (typeof usd !== 'number' || usd < 0 || isNaN(usd)) {
    return '0.00';
  }
  return (usd * rate).toFixed(2);
}

/**
 * ფასის კონვერტაცია EUR -> GEL
 */
export function convertEurToGel(eur, rate = DEFAULT_EUR_GEL_RATE) {
  if (typeof eur !== 'number' || eur < 0 || isNaN(eur)) {
    return '0.00';
  }
  return (eur * rate).toFixed(2);
}

/**
 * ფასის კონვერტაცია ნებისმიერი ვალუტიდან GEL-ში
 */
export function convertToGel(amount, currency, rates = DEFAULT_CURRENCY_RATES) {
  if (typeof amount !== 'number' || amount < 0 || isNaN(amount)) {
    return '0.00';
  }
  const rate = rates[currency] || DEFAULT_USD_GEL_RATE;
  return (amount * rate).toFixed(2);
}

/**
 * მოგების გამოთვლა
 */
export function calculateProfit(salePriceGel, purchasePriceGel) {
  if (typeof salePriceGel !== 'number' || typeof purchasePriceGel !== 'number') {
    return null;
  }
  if (salePriceGel < 0 || purchasePriceGel < 0) {
    return null;
  }

  return (salePriceGel - purchasePriceGel).toFixed(2);
}

/**
 * მოგების გამოთვლა ვალუტიდან
 */
export function calculateProfitFromCurrency(salePriceGel, purchasePrice, currency, rates = DEFAULT_CURRENCY_RATES) {
  if (typeof salePriceGel !== 'number' || typeof purchasePrice !== 'number') {
    return null;
  }
  if (salePriceGel < 0 || purchasePrice < 0) {
    return null;
  }

  const purchasePriceGel = convertToGel(purchasePrice, currency, rates);
  return calculateProfit(salePriceGel, parseFloat(purchasePriceGel));
}

/**
 * თარიღის ფორმატირება
 */
export function formatDate(dateString) {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString('ka-GE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * ფულის ფორმატირება
 */
export function formatCurrency(amount, currency = 'GEL') {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return currency === 'GEL' ? '₾0.00' : '$0.00';
  }
  
  return new Intl.NumberFormat('ka-GE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(amount);
}
